import { useCallback, useEffect, useMemo, useRef, useState, ReactNode } from 'react'

type Props<T> = {
  items: T[]
  renderItem: (item: T, index: number) => ReactNode
  visibleItems?: number
  autoPlay?: boolean
  interval?: number
}

export function InfiniteCarousel<T>({
  items,
  renderItem,
  visibleItems = 3,
  autoPlay = true,
  interval = 3000,
}: Props<T>) {
  const [index, setIndex] = useState(visibleItems)
  const [isTransitioning, setIsTransitioning] = useState(true)
  const trackRef = useRef<HTMLDivElement>(null)

  const extendedItems = useMemo(() => {
    if (items.length === 0) return []
    const start = items.slice(-visibleItems)
    const end = items.slice(0, visibleItems)
    return [...start, ...items, ...end]
  }, [items, visibleItems])

  const next = useCallback(() => {
    setIndex((prev) => prev + 1)
  }, [])

  useEffect(() => {
    if (!isTransitioning) return

    const total = items.length

    if (index >= total + visibleItems) {
      setTimeout(() => {
        setIsTransitioning(false)
        setIndex(visibleItems)
      }, 500)
    }

    if (index < visibleItems) {
      setTimeout(() => {
        setIsTransitioning(false)
        setIndex(total + visibleItems - 1)
      }, 500)
    }
  }, [index, isTransitioning, items.length, visibleItems])

  useEffect(() => {
    if (!isTransitioning) {
      requestAnimationFrame(() => {
        setIsTransitioning(true)
      })
    }
  }, [isTransitioning])

  useEffect(() => {
    if (!autoPlay) return
    const timer = setInterval(next, interval)
    return () => clearInterval(timer)
  }, [autoPlay, interval, next])

  return (
    <div
      style={{ overflow: 'hidden', width: '100%' }}
      onMouseEnter={() => (autoPlay = false)}
      onMouseLeave={() => (autoPlay = true)}
    >
      <div
        ref={trackRef}
        style={{
          display: 'flex',
          transform: `translateX(-${index * (100 / visibleItems)}%)`,
          transition: isTransitioning ? 'transform 0.5s ease' : 'none',
        }}
      >
        {extendedItems.map((item, i) => (
          <div
            key={i}
            style={{
              flex: `0 0 ${100 / visibleItems}%`,
              padding: '0 0.75rem',
            }}
          >
            {renderItem(item, i)}
          </div>
        ))}
      </div>

      {/*   <div style={{ textAlign: 'center', marginTop: 10 }}>
        <button onClick={prev}>←</button>
        <button onClick={next} style={{ marginLeft: 10 }}>
          →
        </button>
      </div> */}
    </div>
  )
}
